/**
 * PostgreSQL timezone normalization and persistence.
 * 
 * The app stores the normalized timezone from login in Dexie (LocalSetting)
 * and uses it for all timelogs instead of reading from the browser each time.
 * 
 * This prevents timezone mismatches when:
 * - Browser timezone changes after login
 * - Different browsers report different formats (e.g. America/Argentina/Buenos_Aires)
 * 
 * Background:
 * - Modern browsers return: 'America/Argentina/Buenos_Aires'
 * - PostgreSQL expects: 'America/Buenos_Aires'
 * - Backend returns error 'if118' when receiving the unsupported format
 */

import { db } from '@/shared/db/database';

/**
 * PostgreSQL does not accept IANA timezones with the 'America/Argentina/' subpath.
 * This map covers the timezones that Intl.DateTimeFormat can return for Argentina.
 * Any unrecognized timezone passes through unchanged.
 */
const PG_TIMEZONE_MAP: Record<string, string> = {
  'America/Argentina/Buenos_Aires':  'America/Buenos_Aires',
  'America/Argentina/Cordoba':       'America/Cordoba',
  'America/Argentina/Salta':         'America/Salta',
  'America/Argentina/Jujuy':         'America/Jujuy',
  'America/Argentina/Tucuman':       'America/Tucuman',
  'America/Argentina/Catamarca':     'America/Catamarca',
  'America/Argentina/La_Rioja':      'America/La_Rioja',
  'America/Argentina/San_Juan':      'America/San_Juan',
  'America/Argentina/Mendoza':       'America/Mendoza',
  'America/Argentina/San_Luis':      'America/Argentina/San_Luis',
  'America/Argentina/Rio_Gallegos':  'America/Argentina/Rio_Gallegos',
  'America/Argentina/Ushuaia':       'America/Argentina/Ushuaia',
};

/**
 * Normalizes an IANA timezone to a PostgreSQL-compatible format.
 * 
 * @param tz - IANA timezone string (e.g. from Intl.DateTimeFormat().resolvedOptions().timeZone)
 * @returns PostgreSQL-compatible timezone string
 * 
 * @example
 * normalizePgTimezone('America/Argentina/Buenos_Aires') // 'America/Buenos_Aires'
 * normalizePgTimezone('America/El_Salvador')            // 'America/El_Salvador' (unchanged)
 */
export function normalizePgTimezone(tz: string): string {
  return PG_TIMEZONE_MAP[tz] ?? tz;
}

/**
 * Dexie LocalSetting keys for stored timezones.
 * 
 * - device_timezone_pg: User's timezone from login (for device enrollment)
 * - marking_timezone_pg: Structure's timezone from selected profile (for timelogs)
 */
const DEVICE_TIMEZONE_KEY = 'device_timezone_pg';
const MARKING_TIMEZONE_KEY = 'marking_timezone_pg';

/**
 * Saves the device timezone to Dexie (user's timezone from login).
 * Used for device enrollment.
 * 
 * @param timezone - PostgreSQL-compatible timezone from user.settings_user.timezone_pg
 */
export async function saveDeviceTimezone(timezone: string): Promise<void> {
  await db.settings.put({
    key: DEVICE_TIMEZONE_KEY,
    value: timezone,
    updated_at: Date.now(),
  });
}

/**
 * Saves the marking timezone to Dexie (structure's timezone from selected profile).
 * Used for timelogs.
 * 
 * @param timezone - PostgreSQL-compatible timezone from structure.timezone_pg
 */
export async function saveMarkingTimezone(timezone: string): Promise<void> {
  await db.settings.put({
    key: MARKING_TIMEZONE_KEY,
    value: timezone,
    updated_at: Date.now(),
  });
}

/**
 * OLD API (v1): Generic saveTimezone - kept for backward compatibility
 * @deprecated Use saveDeviceTimezone or saveMarkingTimezone instead
 */
export async function saveTimezone(timezone: string, normalize = false): Promise<void> {
  const value = normalize ? normalizePgTimezone(timezone) : timezone;
  // Default behavior: save as device timezone for backward compatibility
  await saveDeviceTimezone(value);
}

/**
 * Gets the device timezone from Dexie (user's timezone).
 * Used for device enrollment.
 * Falls back to browser timezone if not found.
 * 
 * @returns PostgreSQL-compatible timezone string
 */
export async function getDeviceTimezone(): Promise<string> {
  try {
    const setting = await db.settings.get(DEVICE_TIMEZONE_KEY);
    if (setting?.value) {
      return setting.value;
    }
  } catch (err) {
    console.warn('[timezone] Failed to read device timezone from Dexie, using browser fallback:', err);
  }
  
  // Fallback: read from browser and normalize
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return normalizePgTimezone(browserTimezone);
}

/**
 * Gets the marking timezone from Dexie (structure's timezone).
 * Used for timelogs.
 * Falls back to device timezone if marking timezone not set.
 * 
 * @returns PostgreSQL-compatible timezone string
 */
export async function getMarkingTimezone(): Promise<string> {
  try {
    const setting = await db.settings.get(MARKING_TIMEZONE_KEY);
    if (setting?.value) {
      return setting.value;
    }
  } catch (err) {
    console.warn('[timezone] Failed to read marking timezone from Dexie, using device timezone fallback:', err);
  }
  
  // Fallback: use device timezone (which may fallback to browser)
  return getDeviceTimezone();
}

/**
 * OLD API (v1): Generic getStoredTimezone - kept for backward compatibility
 * @deprecated Use getDeviceTimezone or getMarkingTimezone instead
 */
export async function getStoredTimezone(): Promise<string> {
  return getDeviceTimezone();
}
